import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingCart } from "lucide-react";
import { useCart } from "@/context/CartContext";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/product-development", label: "Product Development" },
  { to: "/food-consultancy", label: "Food Consultancy" },
  { to: "/raw-material-supply", label: "Raw Material" },
  { to: "/brands", label: "Brands" },
  { to: "/franchise-solutions", label: "Franchise" },
  { to: "/contact", label: "Contact" },
];

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { count } = useCart();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-400 ${
        scrolled
          ? "bg-[rgba(250,247,239,0.92)] backdrop-blur-xl border-b border-[var(--k-line)]"
          : "bg-[rgba(250,247,239,0.55)] backdrop-blur"
      }`}
    >
      <div className="container-k flex items-center justify-between py-4 px-5 md:px-10">
        <Link to="/" className="flex items-center gap-3 group" data-testid="header-brand">
          <div className="w-7 h-7 border border-[var(--k-green)] rotate-45 flex items-center justify-center transition-transform group-hover:rotate-[135deg] duration-700">
            <div className="w-2 h-2 bg-[var(--k-green)] rotate-45" />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-serif-display text-2xl tracking-tight text-[var(--k-text)]">
              KOSTRA
            </span>
            <span className="font-mono-label text-[0.55rem] text-[var(--k-green)] mt-1 hidden sm:inline">
              Hospitality
            </span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7 font-mono-label text-[0.62rem] text-[var(--k-muted)]">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.to.replace(/[^a-z]/g, "") || "home"}`}
              className={({ isActive }) =>
                `transition-colors hover:text-[var(--k-green)] ${
                  isActive ? "text-[var(--k-green)]" : ""
                }`
              }
              end={n.to === "/"}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/raw-material-supply"
            data-testid="header-cart-btn"
            className="relative w-10 h-10 rounded-full border border-[var(--k-line)] flex items-center justify-center hover:border-[var(--k-green)] transition-colors"
            aria-label="Cart"
          >
            <ShoppingCart className="w-4 h-4 text-[var(--k-green)]" strokeWidth={1.6} />
            {count > 0 && (
              <span
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--k-green)] text-[var(--k-bg)] text-[0.6rem] font-semibold flex items-center justify-center"
                data-testid="header-cart-count"
              >
                {count}
              </span>
            )}
          </Link>
          <Link
            to="/contact"
            className="hidden md:inline-flex btn-outline-green !py-2.5 !px-5 !text-[0.62rem]"
            data-testid="header-cta-consult"
          >
            Get Consultation
          </Link>
          <Link
            to="/admin"
            className="hidden md:inline-flex items-center font-mono-label text-[0.6rem] tracking-wider text-[var(--k-muted)] hover:text-[var(--k-green)] transition-colors"
            data-testid="header-admin-login"
          >
            Admin Login
          </Link>
          <button
            onClick={() => setOpen((p) => !p)}
            className="lg:hidden w-10 h-10 rounded-full border border-[var(--k-line)] flex items-center justify-center"
            data-testid="header-menu-btn"
            aria-label="Menu"
          >
            {open ? (
              <X className="w-4 h-4 text-[var(--k-green)]" />
            ) : (
              <Menu className="w-4 h-4 text-[var(--k-green)]" />
            )}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden border-t border-[var(--k-line)] bg-[var(--k-bg)]"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-3">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  className={({ isActive }) =>
                    `font-serif-display text-2xl ${
                      isActive ? "text-[var(--k-green)]" : "text-[var(--k-text)]"
                    }`
                  }
                  data-testid={`mobile-nav-link-${n.to.replace(/[^a-z]/g, "") || "home"}`}
                >
                  {n.label}
                </NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
